import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BUSINESS, CATEGORIES, NAV, shopCategoryUrl } from "@/lib/site";
import { track } from "@/lib/analytics";

const Star = () => <span className="text-brand-red" aria-hidden="true">★</span>;

const TopBar = () => (
  <div className="bg-brand-ink py-2.5" data-testid="top-info-bar">
    <p className="flex flex-wrap items-center justify-center gap-x-3 px-3 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-white sm:text-xs">
      <span>ABCA-licensed medical dispensary</span>
      <Star />
      <span>Open daily from 10 AM</span>
      <span className="hidden sm:inline"><Star /></span>
      <span className="hidden sm:inline">Upstairs at {BUSINESS.street} · 21+</span>
    </p>
  </div>
);

const MobileMenu = ({ onClose }) => (
  <div id="mobile-menu" className="border-t border-neutral-200 bg-white px-5 pb-8 pt-4 lg:hidden" data-testid="mobile-menu">
    <nav aria-label="Mobile" className="flex flex-col">
      {[{ to: "/", label: "Home" }, ...NAV].map((n) => (
        <Link key={n.to} to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`} className="border-b border-neutral-100 py-3.5 font-display text-xl font-bold">
          {n.label}
        </Link>
      ))}
    </nav>
    <Link to="/shop-all" onClick={onClose} className="btn-lime mt-6 w-full" data-testid="mobile-menu-shop-button">Shop now</Link>
    <a href={BUSINESS.phoneHref} className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold" data-testid="mobile-menu-phone-link" onClick={() => track("phone_click", { location: "mobile_menu" })}>
      <Phone className="h-4 w-4" /> {BUSINESS.phone}
    </a>
  </div>
);

const DESKTOP = [
  { to: "/shop-all", label: "Shop" },
  ...CATEGORIES.filter((c) => ["flower", "edibles"].includes(c.slug)).map((c) => ({ to: shopCategoryUrl(c.slug), label: c.name })),
  ...NAV.slice(1),
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <TopBar />
      <div className="border-b-4 border-brand-lime bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6">
          <Link to="/" aria-label="District Gaslight home" data-testid="header-logo-link" className="shrink-0"><Logo className="h-10" /></Link>
          <nav aria-label="Primary" className="hidden flex-1 items-center gap-6 lg:flex">
            {DESKTOP.map((n) => (
              <NavLink key={n.label} to={n.to} end data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`} className="nav-link">
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a href={BUSINESS.phoneHref} aria-label={`Call ${BUSINESS.phone}`} data-testid="header-phone-link" onClick={() => track("phone_click", { location: "header" })} className="hidden h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-neutral-100 md:flex">
              <Phone className="h-5 w-5" />
            </a>
            <Link to="/shop-all" data-testid="header-shop-now-button" onClick={() => track("shop_now_click", { location: "header" })} className="btn-lime !px-5 !py-2.5">Shop now</Link>
            <button type="button" className="flex h-10 w-10 items-center justify-center lg:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && <MobileMenu onClose={() => setOpen(false)} />}
      </div>
    </header>
  );
};
